import {Component, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastService} from '../../services/userInterface/toast.service';
import {LoadingSpinnerService} from '../../services/userInterface/loading-spinner.service';
import {IonCol, IonGrid, IonRow, IonContent, IonSearchbar, IonButton, IonCardTitle, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle } from '@ionic/angular/standalone';
import {CommonModule} from '@angular/common';
import {MatDividerModule} from '@angular/material/divider';
import {HeaderComponent} from '../common/header/header.component';
import {Subscription} from 'rxjs';
import {ErrorHubService} from '../../services/websocket/error-hub.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {LogBookService} from '../../services/error/log-book.service';
import {ErrorDetailPreview} from '../../models/error/ErrorDetailPreview';
import {LogBookErrorListComponent} from '../common/log-book-error-list/log-book-error-list.component';
import { PagedResult } from '../../models/PagedResult';
import { NotFoundException } from '../../models/exception/NotFoundException';
import { LogBookErrorFilter } from '../../models/filter/LogBookErrorFilter';
import { LogBookQueryFilter } from '../../models/filter/LogBookQueryFilter';

@Component({
    selector: 'app-log-book-detail',
    templateUrl: './log-book-detail.component.html',
    styleUrls: ['./log-book-detail.component.css'],
    imports: [IonContent, CommonModule, MatDividerModule, HeaderComponent, LogBookErrorListComponent, IonGrid, IonRow, IonCol, IonSearchbar, TranslateModule, IonButton, IonCard, IonCardContent, IonCardTitle, IonCardHeader, IonCardSubtitle],
    standalone: true,
})
export class LogBookDetailComponent {
    private route: ActivatedRoute = inject(ActivatedRoute);
    private router: Router = inject(Router);

    private toastService: ToastService = inject(ToastService);
    private loadingSpinnerService: LoadingSpinnerService = inject(LoadingSpinnerService);
    private translateService: TranslateService = inject(TranslateService);

    private errorHubService: ErrorHubService = inject(ErrorHubService);
    private logBookService: LogBookService = inject(LogBookService);

    protected initializingTable: boolean = true;
    protected isLoadingErrors: boolean = false;
    private newErrorSubscription?: Subscription;

    public pagedResult!: PagedResult<ErrorDetailPreview>;
    public pageSizeOptions: number[] = [5, 10, 20];

    public logBookQueryFilter: LogBookQueryFilter;

    private logBookId?: string;

    constructor() {
        this.logBookQueryFilter = new LogBookQueryFilter(0, this.pageSizeOptions[0], new LogBookErrorFilter());
    }

    public async ngOnInit() {
        await this.getSelectedLogBook();
    }

    async ionViewWillEnter() {
        await this.initializeErrorHub();
    }

    async ionViewWillLeave() {
        this.newErrorSubscription?.unsubscribe();
    }

    private async initializeErrorHub() {
        await this.errorHubService.initializeHubConnection();
        this.newErrorSubscription = this.errorHubService.newErrorIsRegistered().subscribe({
            next: async () => {
                await this.toastService.showSuccessMessage(this.translateService.instant("toastMessage.success.refreshMessages"));
            },
            error: async () => {
                await this.toastService.showErrorMessage(this.translateService.instant("toastMessage.error.refreshMessages"))
            }
        });
    }

    private async getSelectedLogBook() {
        this.logBookId = this.route.snapshot.paramMap.get("id") ?? undefined;
        if (this.logBookId) {
            this.initializingTable = true;
            await this.loadingSpinnerService.showSpinner();
            this.logBookService.getAllErrorsFromLogBook(this.logBookId, this.logBookQueryFilter).subscribe({
                next: async (errors) => {
                    this.pagedResult = errors;
                    this.initializingTable = false;
                    await this.loadingSpinnerService.hideSpinner();
                },
                error: async (error) => {
                    await this.handleFailedErrorRetrieval(error);
                    this.initializingTable = false;
                    await this.loadingSpinnerService.hideSpinner();
                }
            });
        }
    }

    public getErrorsFromLogBook(logBookQueryFilter: LogBookQueryFilter) {
        if (this.logBookId) {
            this.isLoadingErrors = true;
            this.logBookService.getAllErrorsFromLogBook(this.logBookId, logBookQueryFilter).subscribe({
                next: (errors) => {
                    this.isLoadingErrors = false;
                    this.pagedResult = errors;
                    this.logBookQueryFilter = logBookQueryFilter;
                },
                error: async (error) => {
                    this.isLoadingErrors = false;
                    await this.handleFailedErrorRetrieval(error);
                }
            });
        }
    }

    private async handleFailedErrorRetrieval(error: any) {
        if (error instanceof NotFoundException) {
            const errorMessage: string = this.translateService.instant("components.logBookDetails.notFoundErrorMessage");
            await this.toastService.showErrorMessage(errorMessage);
            this.navigateToPageNotFound();
            return;
        }

        const errorMessage: string = this.translateService.instant("components.logBookDetails.loadingErrorMessage");
        await this.toastService.showErrorMessage(errorMessage);
        await this.navigateToOverview();
    }


    private async navigateToPageNotFound() {
        await this.router.navigate(["/page-not-found"], {replaceUrl: true});
    }

    private async navigateToOverview() {
        await this.router.navigate(['/overview'], {replaceUrl: true});
    }

    public solveAllErrors() {
        if (this.logBookId)
            this.logBookService.solveAllErrorsFromLogBook(this.logBookId).subscribe({
                next: async () => {
                    this.pagedResult.items.forEach((item) => item.isSolved = true);
                    const message: string = this.translateService.instant("toastMessage.success.logBookDetails.solveLogBook");
                    await this.toastService.showSuccessMessage(message);
                },
                error: async () => {
                    const errorMessage: string = this.translateService.instant("toastMessage.error.logBookDetails.solveLogBook");
                    await this.toastService.showErrorMessage(errorMessage);
                }
        })
    }
}
